import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, BladApi } from '../api/client';
import { useLista, type WynikListy } from '../api/lista';
import type { Rola, UzytkownikRekord } from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import { Karta, Komunikat, Ladowanie, Plakietka, Pole, Przycisk, Wybor } from '../components/ui';
import { PrzyciskExcel } from '../components/PrzyciskExcel';
import { dataGodzina, temu } from '../lib/format';

export function Uzytkownicy() {
  const qc = useQueryClient();
  const { uzytkownik: ja } = useAuth();
  const [formularz, setFormularz] = useState(false);
  const [nowe, setNowe] = useState<{ login: string; haslo: string } | null>(null);
  const [blad, setBlad] = useState<string | null>(null);

  const uzytkownicy = useQuery({
    queryKey: ['uzytkownicy'],
    queryFn: () => api.get<{ pozycje: UzytkownikRekord[] }>('/uzytkownicy'),
  });
  const role = useLista({
    queryKey: ['role'],
    queryFn: () => api.get<{ pozycje: Rola[] }>('/role'),
  });

  const reset = useMutation({
    mutationFn: (id: number) => api.post<{ hasloStartowe: string }>(`/uzytkownicy/${id}/reset-hasla`),
    onSuccess: (odp, id) => {
      const u = uzytkownicy.data?.pozycje.find((x) => x.id === id);
      setNowe({ login: u?.login ?? '', haslo: odp.hasloStartowe });
      void qc.invalidateQueries({ queryKey: ['uzytkownicy'] });
    },
  });

  const odblokuj = useMutation({
    mutationFn: (id: number) => api.post(`/uzytkownicy/${id}/odblokowanie`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['uzytkownicy'] }),
  });

  // Wspólna dla przełącznika stanu i zmiany roli — obie tylko nadpisują jedno
  // pole w tym samym żądaniu, reszta rekordu jedzie bez zmian.
  const zapisz = useMutation({
    mutationFn: ({ u, zmiana }: { u: UzytkownikRekord; zmiana: { roleId?: number; isActive?: boolean } }) =>
      api.put(`/uzytkownicy/${u.id}`, {
        fullName: u.fullName, email: u.email ?? '', phone: u.phone ?? '',
        roleId: u.role.id, isActive: u.isActive, version: u.version,
        ...zmiana,
      }),
    onSuccess: () => { setBlad(null); void qc.invalidateQueries({ queryKey: ['uzytkownicy'] }); },
    onError: (e) => setBlad(e instanceof BladApi ? e.message : 'Nie udało się zapisać zmiany.'),
  });

  if (uzytkownicy.isLoading || role.wczytywanie) return <Ladowanie />;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-kamien-900">Użytkownicy</h1>
          <p className="mt-1 text-sm text-kamien-500">
            Konta zakłada wyłącznie administrator. Hasło startowe pokazuje się jeden raz.
          </p>
        </div>
        <PrzyciskExcel dzial="uzytkownicy" className="ml-auto" />
        <Przycisk wariant="glowny" onClick={() => setFormularz(true)}>Nowe konto</Przycisk>
      </div>

      {blad && <Komunikat rodzaj="blad">{blad}</Komunikat>}

      {nowe && (
        <Komunikat rodzaj="uwaga" tytul={`Hasło startowe dla konta „${nowe.login}”`}>
          <p className="mt-1 font-mono text-base tracking-wide text-kamien-900">{nowe.haslo}</p>
          <p className="mt-2">
            Przekaż je użytkownikowi teraz — po zamknięciu tego komunikatu nie da się go
            odczytać, bo w bazie jest wyłącznie skrót. Przy pierwszym logowaniu system
            wymusi zmianę.
          </p>
          <Przycisk wariant="cichy" className="mt-2 px-0" onClick={() => setNowe(null)}>
            Zamknij
          </Przycisk>
        </Komunikat>
      )}

      {formularz && (
        <NoweKonto
          role={role}
          zamknij={() => setFormularz(false)}
          gotowe={(login, haslo) => { setNowe({ login, haslo }); setFormularz(false); }}
        />
      )}

      <Karta>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
              <tr>
                <th className="px-5 py-3 font-medium">Login</th>
                <th className="px-5 py-3 font-medium">Imię i nazwisko</th>
                <th className="px-5 py-3 font-medium">Rola</th>
                <th className="px-5 py-3 font-medium">Stan</th>
                <th className="px-5 py-3 font-medium">Ostatnie logowanie</th>
                <th className="px-5 py-3 font-medium">Działania</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-kamien-200">
              {uzytkownicy.data?.pozycje.map((u) => {
                const zablokowany = u.lockedUntil && new Date(u.lockedUntil) > new Date();
                return (
                  <tr key={u.id} className={u.isActive ? '' : 'text-kamien-400'}>
                    <td className="px-5 py-3 font-medium text-kamien-900">{u.login}</td>
                    <td className="px-5 py-3">{u.fullName}</td>
                    <td className="px-5 py-3">
                      <select
                        aria-label={`Rola użytkownika ${u.fullName}`}
                        title={u.id === ja?.id ? 'Nie możesz zmienić własnej roli — poproś innego administratora.' : undefined}
                        className="min-h-9 rounded-md border border-kamien-300 bg-white px-2 text-xs text-kamien-900
                          focus:outline-none focus:ring-2 focus:ring-bordo-600
                          disabled:cursor-not-allowed disabled:border-transparent disabled:bg-transparent disabled:px-0 disabled:text-kamien-400"
                        value={u.role.id}
                        disabled={u.id === ja?.id || zapisz.isPending}
                        onChange={(e) => {
                          const roleId = Number(e.target.value);
                          if (roleId !== u.role.id) zapisz.mutate({ u, zmiana: { roleId } });
                        }}
                      >
                        {role.pozycje.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {!u.isActive && <Plakietka>wyłączone</Plakietka>}
                        {zablokowany && <Plakietka ton="blad">zablokowane</Plakietka>}
                        {u.mustChangePassword && <Plakietka ton="uwaga">hasło startowe</Plakietka>}
                        {u.isActive && !zablokowany && !u.mustChangePassword && (
                          <Plakietka ton="ok">czynne</Plakietka>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-kamien-600" title={dataGodzina(u.lastLoginAt)}>
                      {u.lastLoginAt ? temu(u.lastLoginAt) : 'nigdy'}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-2">
                        <Przycisk
                          wariant="cichy" className="min-h-9 px-2 text-xs"
                          onClick={() => reset.mutate(u.id)}
                          disabled={reset.isPending}
                        >
                          Reset hasła
                        </Przycisk>
                        {zablokowany && (
                          <Przycisk
                            wariant="cichy" className="min-h-9 px-2 text-xs"
                            onClick={() => odblokuj.mutate(u.id)}
                          >
                            Odblokuj
                          </Przycisk>
                        )}
                        <Przycisk
                          wariant="cichy" className="min-h-9 px-2 text-xs"
                          onClick={() => zapisz.mutate({ u, zmiana: { isActive: !u.isActive } })}
                        >
                          {u.isActive ? 'Wyłącz' : 'Włącz'}
                        </Przycisk>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Karta>
    </div>
  );
}

function NoweKonto({ role, zamknij, gotowe }: {
  role: WynikListy<Rola>; zamknij: () => void; gotowe: (login: string, haslo: string) => void;
}) {
  const qc = useQueryClient();
  const [dane, setDane] = useState({
    login: '', fullName: '', email: '', phone: '',
    roleId: String(role.pozycje.find((r) => r.code === 'MAGAZYNIER')?.id ?? role.pozycje[0]?.id ?? ''),
  });
  const [bledy, setBledy] = useState<Record<string, string>>({});
  const [blad, setBlad] = useState<string | null>(null);

  const utworz = useMutation({
    mutationFn: () =>
      api.post<{ uzytkownik: UzytkownikRekord; hasloStartowe: string }>('/uzytkownicy', {
        ...dane, roleId: Number(dane.roleId),
      }),
    onSuccess: (odp) => {
      void qc.invalidateQueries({ queryKey: ['uzytkownicy'] });
      gotowe(odp.uzytkownik.login, odp.hasloStartowe);
    },
    onError: (e) => {
      if (e instanceof BladApi) {
        setBledy(e.bledyPol);
        if (Object.keys(e.bledyPol).length === 0) setBlad(e.message);
      }
    },
  });

  return (
    <Karta tytul="Nowe konto" opis="Hasło startowe wygeneruje system">
      <form
        className="grid gap-4 px-5 py-5 sm:grid-cols-2"
        onSubmit={(e) => { e.preventDefault(); setBlad(null); setBledy({}); utworz.mutate(); }}
      >
        {blad && <div className="sm:col-span-2"><Komunikat rodzaj="blad">{blad}</Komunikat></div>}

        <Pole etykieta="Login" value={dane.login} blad={bledy.login}
          onChange={(e) => setDane({ ...dane, login: e.target.value })} required />
        <Pole etykieta="Imię i nazwisko" value={dane.fullName} blad={bledy.fullName}
          onChange={(e) => setDane({ ...dane, fullName: e.target.value })} required />
        <Pole etykieta="E-mail" type="email" value={dane.email} blad={bledy.email}
          onChange={(e) => setDane({ ...dane, email: e.target.value })} />
        <Pole etykieta="Telefon" value={dane.phone} blad={bledy.phone}
          onChange={(e) => setDane({ ...dane, phone: e.target.value })} />
        <Wybor etykieta="Rola" value={dane.roleId} blad={bledy.roleId ?? role.blad}
          onChange={(e) => setDane({ ...dane, roleId: e.target.value })}>
          {role.pozycje.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </Wybor>

        <div className="flex items-end gap-3 sm:col-span-2">
          <Przycisk type="submit" wariant="glowny" disabled={utworz.isPending}>
            {utworz.isPending ? 'Zakładanie...' : 'Załóż konto'}
          </Przycisk>
          <Przycisk type="button" wariant="cichy" onClick={zamknij}>Anuluj</Przycisk>
        </div>
      </form>
    </Karta>
  );
}
